import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { useAuth } from '@/context/AuthContext';
import WalletPanel from './components/WalletPanel';

const payHero =
  'https://readdy.ai/api/search-image?query=Modern%20African%20digital%20payment%20platform%20interface%2C%20mobile%20money%20transaction%2C%20secure%20wallet%20concept%2C%20fintech%20abstract%20visual%20with%20warm%20golden%20lighting%2C%20clean%20minimal%20dark%20background%2C%20professional%20editorial%20photography&width=1600&height=800&seq=pay-hero-01&orientation=landscape';

const features = [
  { icon: 'ri-wallet-3-line', title: 'Portefeuille Numérique', description: 'Solde disponible, solde en attente, historique complet. Un portefeuille unique pour tout l\'écosystème.' },
  { icon: 'ri-add-circle-line', title: 'Dépôts', description: 'Rechargez votre portefeuille via Mobile Money, carte bancaire, PayDunya et autres passerelles de paiement.' },
  { icon: 'ri-arrow-down-circle-line', title: 'Retraits', description: 'Retirez vos fonds vers Mobile Money, banque ou autre méthode de votre choix, rapidement et en toute sécurité.' },
  { icon: 'ri-exchange-line', title: 'Transferts P2P', description: 'Envoyez et recevez de l\'argent instantanément entre utilisateurs WorldApeiron, avec vérification automatique.' },
  { icon: 'ri-coins-line', title: 'Multi-devises', description: 'Gérez plusieurs devises : FCFA, USD, EUR, GBP, CAD. Conversion automatique avec taux de change intégré.' },
  { icon: 'ri-refund-2-line', title: 'Cashback & Fidélité', description: 'Gagnez des points à chaque achat et recevez du cashback automatique sur vos transactions éligibles.' },
  { icon: 'ri-bill-line', title: 'Facturation PDF', description: 'Génération automatique de factures, reçus de paiement et reçus de retrait au format PDF téléchargeable.' },
  { icon: 'ri-shield-keyhole-line', title: 'Sécurité Avancée', description: '2FA, chiffrement des données, protection anti-fraude, limites de transaction et journalisation complète.' },
  { icon: 'ri-global-line', title: 'Paiement Écosystème', description: 'Payez dans tous les modules : Shop, Work, Learn, Cloud, AI, Forum. Une seule balance, un seul compte.' },
];

const related = [
  { name: 'Shop', path: '/shop', icon: 'ri-shopping-bag-3-line' },
  { name: 'Work', path: '/work', icon: 'ri-briefcase-4-line' },
  { name: 'Learn', path: '/learn', icon: 'ri-graduation-cap-line' },
  { name: 'Cloud', path: '/cloud', icon: 'ri-cloud-line' },
];

function PayPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const scrollToWorkspace = () => {
    document.getElementById('pay-workspace')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={payHero} alt="WorldApeiron Pay" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">
              WorldApeiron Écosystème
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4 leading-tight">
              World<span className="text-accent-400">Pay</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground-400 mb-3">
              Payez. Recevez. Gérez. Simplement.
            </p>
            <p className="text-base text-foreground-400 max-w-xl mb-8 leading-relaxed">
              WorldApeiron Pay est le portefeuille numérique central de l'écosystème. Déposez,
              retirez, transférez et payez dans tous les modules avec une sécurité bancaire.
              Multi-devises, cashback et fidélité intégrés.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {loading ? (
                <div className="px-8 py-3.5">
                  <i className="ri-loader-4-line animate-spin text-2xl text-accent-400" />
                </div>
              ) : user ? (
                <button
                  onClick={scrollToWorkspace}
                  className="px-8 py-3.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap text-sm md:text-base"
                >
                  Accéder à mon portefeuille
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/auth/register')}
                    className="px-8 py-3.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Créer mon portefeuille
                  </button>
                  <button
                    onClick={() => navigate('/auth/login')}
                    className="px-8 py-3.5 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Se connecter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pub */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <AdsBanner pageKey="pay" />
      </section>

      {/* Espace portefeuille (connecté) */}
      {!loading && user && (
        <section id="pay-workspace" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="text-center mb-10 md:mb-12">
              <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
                Mon espace
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
                Votre portefeuille
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto">
                Gérez votre solde, effectuez des dépôts et retraits, et suivez votre historique.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <WalletPanel />
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
              Fonctionnalités
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Tout ce dont vous avez besoin
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-400/40 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500/10 mb-4">
                  <i className={`${feature.icon} text-2xl text-accent-400`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section className="py-16 md:py-20 bg-background-50 border-t border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-8 text-center">
            Découvrez aussi
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((mod) => (
              <button
                key={mod.name}
                onClick={() => navigate(mod.path)}
                className="flex items-center gap-3 px-5 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-400/40 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10">
                  <i className={`${mod.icon} text-xl text-accent-400`} />
                </div>
                <span className="font-heading font-semibold text-sm text-foreground-50">
                  {mod.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PayPage;